"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { getTierDisplayInfo } from "@/lib/account-tier-service";
import { getUsersList, type UserListOptions, type UserListResult } from "@/lib/admin-user-service";
import type { AccountTier, UserProfileDocument } from "@/lib/types";
import { Timestamp, type DocumentData, type DocumentSnapshot } from "firebase/firestore";
import { ArrowUpDown, ChevronRight, Loader2, Search, Sliders } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { EditUserDialog } from "./EditUserDialog";
import { UserLimitOverridesDialog } from "./UserLimitOverridesDialog";

type SortField = "email" | "displayName" | "createdAt" | "accountTier";
type SortDirection = "asc" | "desc";

export function UserManagementGrid() {
  const [users, setUsers] = useState<UserProfileDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [tierFilter, setTierFilter] = useState<AccountTier | "all">("all");
  const [editingUser, setEditingUser] = useState<UserProfileDocument | null>(null);
  const [managingLimitsUser, setManagingLimitsUser] = useState<{ id: string; email: string } | null>(null);

  const loadUsers = useCallback(
    async (reset = false) => {
      setLoading(true);
      setError(null);

      try {
        const options: UserListOptions = {
          pageSize: 20,
          sortField,
          sortDirection,
          filters: {
            accountTier: tierFilter !== "all" ? tierFilter : undefined,
            search: searchQuery || undefined,
          },
          startAfterDoc: reset ? undefined : (lastDoc ?? undefined),
        };

        const result: UserListResult = await getUsersList(options);
        setUsers((prev) => (reset ? result.users : [...prev, ...result.users]));
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error("Failed to load users", err);
        setError(err instanceof Error ? err.message : "Failed to load users");
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, sortField, sortDirection, tierFilter, lastDoc],
  );

  useEffect(() => {
    loadUsers(true);
  }, [loadUsers]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleEdit = (user: UserProfileDocument) => {
    setEditingUser(user);
  };

  const handleUserUpdated = () => {
    loadUsers(true);
    setEditingUser(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={tierFilter} onValueChange={(value) => setTierFilter(value as AccountTier | "all")}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="full_pro">Full Pro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-2"
                  onClick={() => handleSort("displayName")}
                >
                  User
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-2"
                  onClick={() => handleSort("email")}
                >
                  Email
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-2"
                  onClick={() => handleSort("accountTier")}
                >
                  Account Tier
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Recipe Limit</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-2"
                  onClick={() => handleSort("createdAt")}
                >
                  Created
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && users.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-10 w-10 rounded-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users
                .filter((user) => {
                  // Client-side search filter
                  if (!searchQuery) return true;
                  const searchLower = searchQuery.toLowerCase();
                  return (
                    user.email.toLowerCase().includes(searchLower) ||
                    user.displayName.toLowerCase().includes(searchLower)
                  );
                })
                .map((user) => {
                const tierInfo = getTierDisplayInfo(user.accountTier);
                const maxRecipes = tierInfo.maxRecipesPerBox === null ? "Unlimited" : tierInfo.maxRecipesPerBox;
                
                let createdDate = "N/A";
                if (user.createdAt) {
                  if (user.createdAt instanceof Timestamp) {
                    createdDate = user.createdAt.toDate().toLocaleDateString();
                  } else if (user.createdAt instanceof Date) {
                    createdDate = user.createdAt.toLocaleDateString();
                  } else if (typeof user.createdAt === 'string' || typeof user.createdAt === 'number') {
                     createdDate = new Date(user.createdAt).toLocaleDateString();
                  }
                }

                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.avatarURL} alt={user.displayName} />
                          <AvatarFallback>
                            {user.displayName?.charAt(0)?.toUpperCase() ?? user.email?.charAt(0)?.toUpperCase() ?? "U"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{user.displayName || "No name"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.accountTier === "full_pro" ? "default" : "secondary"}>
                        {tierInfo.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{maxRecipes}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {user.isAdmin && <Badge variant="outline">Admin</Badge>}
                        {user.isBetaUser && <Badge variant="outline">Beta</Badge>}
                        {!user.isAdmin && !user.isBetaUser && (
                          <span className="text-xs text-muted-foreground">Standard</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{createdDate}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(user)}>
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setManagingLimitsUser({ id: user.id!, email: user.email })}
                          title="Manage Limits"
                        >
                          <Sliders className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!loading && users.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {users.length} user{users.length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadUsers(false)}
              disabled={!hasMore || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ChevronRight className="mr-2 h-4 w-4" />
                  Load More
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* User Limit Overrides Dialog */}
      {managingLimitsUser && (
        <UserLimitOverridesDialog
          userId={managingLimitsUser.id}
          userEmail={managingLimitsUser.email}
          open={!!managingLimitsUser}
          onOpenChange={(open) => !open && setManagingLimitsUser(null)}
        />
      )}

      {/* Edit User Dialog */}
      {editingUser && (
        <EditUserDialog
          user={editingUser}
          open={!!editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
}

